// React
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Routing
import { useParams } from "react-router-dom";

// Context
import { DashboardColorContext } from "../context/DashboardColorContext";
import LayersContext from "../context/LayersContext";
import MetadataContext from "../context/MetadataContext";
import MetricCategoriesContext from "../context/MetricCategoriesContext";
import { useOrgContext } from "../context/OrgIdContext";

// Hooks
import useFetchData from "../hooks/useFetchData";

// Utilities
import { trackEvent } from "../analytics/utils/analytics";
import { handleExcelDownload } from "../utils/excelExportModule";
import { tabColors } from "../utils/helpers";
import { categorizeData, getSvgMap } from "../utils/dashboardUtils";

// Assets
import downloadSvg from "../assets/download.svg";
import layerSvg from "../assets/layer.svg";
import mapSvg from "../assets/map.svg";
import printSvg from "../assets/print.svg";

// Components
import ClearMapSelectionModal from "../components/ClearMapSelectionModal";
import DownloadLoading from "../components/DownloadLoading";
import IndexBanner from "../components/IndexBanner";
import LayerPanel from "../components/layers/LayerPanel";
import LayersModal from "../components/layers/LayersModal";
import LegendMetricModal from "../components/LegendMetricModal";
import LiveRegion from "../components/accessibility/LiveRegion";
import MapItemDetailsModal from "../components/MapItemDetailsModal";
import MappableMetricsModal from "../components/mappableModal/MappableMetricsModal";
import TabMenu from "../components/TabMenu";

// Skeletons
import MetricCardSkeleton from "../components/skeletons/MetricCardSkeleton";
import TableSkeleton from "../components/skeletons/TableSkeleton";
import TabMenuSkeleton from "../components/skeletons/TabMenuSkeleton";

// Panels
import AccessibilityMetrics from "../components/panels/AccessibilityMetrics";
import DemographicMetrics from "../components/panels/DemographicMetrics";
import EconomyMetrics from "../components/panels/EconomyMetrics";
import EducationMetrics from "../components/panels/EducationMetrics";
import HousingMetrics from "../components/panels/HousingMetrics";

// Modals
import MetricsModal from "./MetricsModal";
import ProgrammaticModal from "./ProgrammaticModal";

const Dashboard = () => {
    const isIframe = window.self !== window.top;
    // const navigate = useNavigate();
    const { tab } = useParams<{ tab: string }>();
    const { orgId, locationId } = useOrgContext();
    const { layers } = useContext(LayersContext);
    const { metricCategories, loading: categoriesLoading } = useContext(MetricCategoriesContext);
    // Fetch metric metadata for the current org
    const { metadata, loading: metadataLoading } = useContext(MetadataContext);
    const prevViewRef = useRef<string | null>(null);

    // Message prop state
    const [currentTab, setCurrentTab] = useState<string>("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [legendUrl, setLegendUrl] = useState('');
    const [selectedArea, setSelectedArea] = useState('');
    const [siteUrl, setSiteUrl] = useState('');
    const [legendId, setLegendId] = useState(0);

    // Modal state
    const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
    const [isMappableModalOpen, setIsMappableModalOpen] = useState(false);
    const [isLayersModalOpen, setIsLayersModalOpen] = useState(false);
    const [isProgrammaticModalOpen, setIsProgrammaticModalOpen] = useState(false);

    // Excel state
    const [shouldDownloadExcel, setShouldDownloadExcel] = useState(false);
    const [showDownloadLoading, setShowDownloadLoading] = useState(false);

    // Map item details modal state
    const [mapItemDetailModalOpen, setMapItemDetailModalOpen] = useState(false);
    const [mapItemParams, setMapItemParams] = useState({ layerId: null, id: null });    

    // Geographic levels and exclusion lists defined for excel (will vary by project)
    const geographicLevels = [
        ...new Set(metadata?.flatMap((metric) => 
          metric.versions.map((version) => version.level_of_detail)
        ))
    ];

    const summaryExclusionList: string[] = [];
    const disaggregationExclusionList: string[] = [];
    const geographyExclusionList: string[] = [];

    const { indicators, loading, dashboardView, geoDetails, geoTag, selectionActive, lodMissing } = useFetchData(currentTab || '');

    const tabs = metricCategories;
    const [activeTab, setActiveTab] = useState(tabs[0]);

    // "Ready" flag and tracking metric tabs
    const isLoading = (!currentTab && layers.length === 0) ||
    categoriesLoading ||
    metadataLoading ||
    loading;

    const isMetricTab = useMemo(() => {
        if (categoriesLoading || !metricCategories.length || !currentTab) return undefined;
        
        return (
            !!currentTab &&
            metricCategories.length > 0 &&
            metricCategories.map(c => c.toLowerCase()).includes(currentTab.toLowerCase())
        );
    }, [metricCategories, currentTab]);

    const sectionRefs = useMemo(() => {
        const refs: Record<string, React.RefObject<HTMLDivElement>> = {};
        tabs.forEach(tab => {
            refs[tab] = React.createRef<HTMLDivElement>();
        });
        return refs;
    }, [tabs]);

    // #region React useEffects
    useEffect(() => {
        if (!isIframe && tabs.length > 0) {
            if (tab && tabs.includes(tab)) {
                setCurrentTab(tab.toLowerCase());
            } else if (!currentTab) {
                setCurrentTab(tabs[0]);
            }
        }
    }, [tab, isIframe, tabs]);

    useEffect(() => {
        if (isIframe && dashboardView && tabs.length > 0) {
            // Check if dashboardView matches a layer_name and add it to tabs
            const foundLayer = layers.find((layer) => layer.layer_name.toLowerCase() === dashboardView.toLowerCase());
    
            // If foundLayer is present, update the tabs
            if (foundLayer && !tabs.includes(dashboardView)) {
                const updatedTabs = [...tabs, dashboardView];
                setCurrentTab(updatedTabs.find((t) => t.toLowerCase() === dashboardView.toLowerCase()) || updatedTabs[0]);
            } else {
                setCurrentTab(tabs.find((t) => t.toLowerCase() === dashboardView.toLowerCase()) || tabs[0]);
            }
        } else {
            // When not in iframe, just ensure the currentTab is set based on the `tab` param
            setCurrentTab(tabs.find((t) => t.toLowerCase() === currentTab?.toLowerCase()) || tabs[0]);
        }
    }, [dashboardView, isIframe, tabs, layers]);

    // Combine data from all sources
    const allDataAvailable = useMemo(() => {
        return !loading && indicators;
    }, [loading, indicators]);


    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data.message === 'pdf') {
                setPdfUrl(event.data.params.url);
                setLogoUrl(event.data.params.logoUrl || '');
                setLegendUrl(event.data.params.legendUrl || '');
                setSelectedArea(event.data.params.selectedArea || '');
                setSiteUrl(event.data.params.siteUrl || '');
                setIsModalOpen(true);

                // Send message to expand
                window.parent.postMessage({ 
                    message: "expand",
                    mode: "fullscreen"
                }, '*');
                console.log('expand message sent');
            } else if (event.data.message === 'metric-modal') {

                // Legend metadata info icon event
                console.log('Message received from parent app inside legend metric modal:', event.data);
                setLegendId(event.data.params.id);
            } else if (event.data.message === 'excel') {
                // Get selected area
                const sArea = event.data?.params?.selectedArea;
                // Show loading modal
                setShowDownloadLoading(true);

                if (!loading) {
                    // Execute excel download script
                    handleExcelDownload(orgId, locationId, geoDetails, geoTag, metricCategories, sArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                        setShowDownloadLoading(false);
                    });
                } else {
                    console.log('Data is still loading, will attempt to download Excel later.');
                    setShouldDownloadExcel(true); // Set flag to retry download
                }
            } else if (event.data.message === 'pointLayerDetails') {
                console.log('point layer message:', event.data);
                const { layerId, id } = event.data.params || {};
                if (layerId && id) {
                    setMapItemParams({ layerId, id });
                    setMapItemDetailModalOpen(true);
                }
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
          window.removeEventListener('message', handleMessage);
        };
    }, [allDataAvailable, geoDetails]);

    useEffect(() => {
        if (!loading && shouldDownloadExcel) {
            setShowDownloadLoading(true);
            handleExcelDownload(orgId, locationId, geoDetails, geoTag, metricCategories, selectedArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                setShouldDownloadExcel(false); 
                setShowDownloadLoading(false);
            });
        }
    }, [loading, shouldDownloadExcel, geoDetails]);

    // Close map item detail modal when dashboard panel changes
    useEffect(() => {
        if (prevViewRef.current !== null && prevViewRef.current !== dashboardView) {
            setMapItemDetailModalOpen(false);
        }
        // Update dashboard view ref
        prevViewRef.current = dashboardView;
    }, [dashboardView]);

    useEffect(() => {
        const handleScroll = () => {
            let closestTab = tabs[0];
            let minDistance = Infinity;

            for (const tab of tabs) {
            const section = sectionRefs[tab]?.current;
            if (section) {
                const rect = section.getBoundingClientRect();

                const distanceFromTop = Math.abs(rect.top - 96);
                // Always pick the section *closest to offset top*
                if (distanceFromTop < minDistance) {
                    minDistance = distanceFromTop;
                    closestTab = tab;
                }
            }
            }

            setActiveTab(closestTab);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // trigger once on load

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [tabs, sectionRefs]);
    //#endregion

    //#region Google Analytics Event Tracking
    useEffect(() => {
        if (!activeTab) return;

        trackEvent("category_view", {
            category: activeTab,
            geo: geoTag?.value,
            metric_count: indicators.length,
            lod_missing: lodMissing,
        });
    }, [activeTab]);

    useEffect(() => {
        if (!activeTab) return;

        const start = Date.now();

        return () => {
            const seconds = Math.round((Date.now() - start) / 1000);

            trackEvent("category_time_spent", {
            category: activeTab,
            seconds,
            geo: geoTag?.value,
            });
        };
    }, [activeTab]);
    //#endregion

    //#region Event Handlers
    const handleTabClick = (tab: string) => {
        const section = sectionRefs[tab]?.current;
        if (section) {
            const offset = 96;
            const top = section.getBoundingClientRect().top + window.pageYOffset - offset;

            window.scrollTo({
                top,
                behavior: 'smooth',
            });
        }
    };

    const closeLegendModal = () => {
        setIsLegendModalOpen(false);
        setLegendId(-1);
    };

    const closeModal = (e: React.MouseEvent<HTMLButtonElement>) => {
        let origin = '*';

        e.preventDefault();
        setIsModalOpen(false);
        // Send message to expand
        window.parent.postMessage({ message: "expand" }, origin);
        console.log('expand message sent');
    };

    const handleDownloadNoIframe = () => {
        // Show loading modal
        setShowDownloadLoading(true);

        if (!loading) {
            // Execute excel download script
            handleExcelDownload(orgId, locationId, geoDetails, geoTag, metricCategories, selectedArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                setShowDownloadLoading(false);
            });
        }
    };

    const handlePrintNoIframe = () => {
        setPdfUrl('');
        setLogoUrl('');
        setLegendUrl('');
        setSelectedArea('');
        setSiteUrl('');
        setIsModalOpen(true);
    };

    const handleShowLayers = () => {
        setIsLayersModalOpen((prevState) => !prevState);
    };

    const handleMappableModal = () => {
        setIsMappableModalOpen(true);
    }

    const closeMappableModal = () => {
        setIsMappableModalOpen(false);
    }

    const closeMapItemModal = () => setMapItemDetailModalOpen(false);

    // Programmatic PDF Modal Handlers
    const openProgrammaticModal = () => {
        setIsProgrammaticModalOpen(true);
        window.parent.postMessage(
            {
            message: "expand",
            mode: "fullscreen",
            },
            "*"
        );
        console.log('expand message sent');
    };

    const closeProgrammaticModal = () => {
        setIsProgrammaticModalOpen(false);
        let origin = '*';
        // Send message to expand
        window.parent.postMessage({ message: "expand" }, origin);
        console.log('expand message sent');
    };
    //#endregion

    const categorizedData = categorizeData(indicators);

    const renderMetrics = (tab: string) => {
        if (!metricCategories || metricCategories.length === 0) {
            if (layers.length > 0) {
                return (
                    <div className="flex-grow overflow-y-auto px-4">
                        <LayerPanel
                            sharedView={true}
                            dashboardView={tab}
                            onPrintClick={openProgrammaticModal}
                        />
                    </div>
                );
            }

            return null;
        }

        // Show modal to clear map selection if the current active selection is missing data or invalid (Small area problem)
        if (lodMissing) {
            return <ClearMapSelectionModal/>
        }

        // Render Layer Panel if dashboardview is not a metric category
        const isMetricTab = metricCategories.map(c => c.toLowerCase()).includes(tab.toLowerCase());

        if (!isMetricTab) {
            return (
                <div className="flex-grow overflow-y-auto px-4">
                    <LayerPanel
                        sharedView={true}
                        dashboardView={tab}
                        onPrintClick={openProgrammaticModal}
                    />
                </div>
            );
        }

        let metricsForTab = indicators; // Get metrics for the current tab

        // Find the indexMetric from metadata based on the current tab
        const indexMetricId = metadata?.find((metric) => metric.metric_source_type === "index" && metric.metric_category?.toLowerCase() === tab.toLowerCase())?.id;

        // Find the full indicator object that matches indexMetric
        const indexMetricData = indicators.find(metric => metric.id === Number(indexMetricId));

        // Exclude the index metric from the list of metrics
        if (indexMetricId) {
            metricsForTab = metricsForTab.filter((metric) => metric.id !== indexMetricId);
        }

        const noMetrics =
        !isLoading &&
        isMetricTab &&
        indicators.length > 0 &&
        metricsForTab.length === 0;

        const color = tabColors[tab] || "#337AB7";

        // Map tab names to corresponding components
        const metricsComponents: Record<string, JSX.Element> = {
            Economy: <EconomyMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Education: <EducationMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Accessibility: <AccessibilityMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            People: <DemographicMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Housing: <HousingMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />
        };

        return (
            <div className="bg-[#f8f8fa]">
                {indexMetricData && (
                    <div className="mb-4">
                        <IndexBanner
                            key={indexMetricId}
                            metric={indexMetricData}
                            svg={undefined}
                            disaggData={indicators}
                            geoDetails={geoDetails}
                            geoTag={geoTag}
                            selectionActive={selectionActive}
                        />
                    </div>
                )}
                <div>
                    {noMetrics ? (
                        <div className="flex items-center justify-center text-sm text-color-cai-dashboard">
                            No metrics available for {tab}.
                        </div>
                    ) : (
                        <DashboardColorContext.Provider value={color}>
                            {metricsComponents[tab]}
                        </DashboardColorContext.Provider>
                    )}
                </div>
            </div>
        );
    };

    // Set active tab to current tab if present
    // const activeTab = currentTab?.toLowerCase() || tabs[0];

    return (
        <div className={`${!isIframe ? 'max-w-[456px] mx-auto print:max-w-full' : ''}`}>
            <div className="flex mr-4 items-center justify-end ">
            </div>
            {/* Functional Icons for Non-iframe View */}
            {!isIframe && (
                <div className="my-2 mx-5 flex items-center justify-end gap-3">
                    {layers.length > 0 && <img src={layerSvg} alt="layer icon" title="Show metric layers" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleShowLayers() }}/> }
                    <img src={printSvg} alt="print icon" title="Print PDF" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handlePrintNoIframe() }}/>
                    <img src={downloadSvg} alt="download icon" title="Download Excel" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleDownloadNoIframe() }}/>
                    <img src={mapSvg} alt="map icon" title="Show Mappable Metrics" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleMappableModal() }}/>
                </div>
            )}

            {/* Tab menu for dashboard categories */}
            <div className="sticky top-0 z-10 bg-white">
                {tabs.length > 0 
                ? <TabMenu
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabClick={handleTabClick}
                    // setCurrentTab={setCurrentTab}
                    // navigate={navigate}
                />
                : <TabMenuSkeleton/>
                }
            </div>

            {/* Render current dashboard view */}
            <div className="no-print pb-[600px]">
                {!isModalOpen && !isProgrammaticModalOpen && (
                    isLoading ? (
                        isMetricTab === undefined ? null :
                        isMetricTab ? (
                            <>
                                {/* Accessible live announcement for loading */}
                                <LiveRegion message={`Loading metrics for ${currentTab}...`} />

                                <div className="mx-auto px-4 pb-8 pt-4">
                                    <div className="grid grid-cols-2 gap-4 w-full">
                                        {[...Array(6)].map((_, i) => (
                                            <MetricCardSkeleton key={i} />
                                        ))}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Accessible live announcement for loading */}
                                <LiveRegion message={`Loading metrics for ${currentTab}...`} />
                                
                                <div className="mx-4">
                                    <TableSkeleton />
                                </div>
                            </>
                        )
                    ) : (
                        tabs.map((tab) => (
                            <div key={tab} ref={sectionRefs[tab]} id={`section-${tab}`}>
                                {renderMetrics(tab)}
                            </div>
                        ))
                    )
                )}
            </div>


            {/* #region: Modals */}
            <LegendMetricModal
                id={legendId}
                isOpen={isLegendModalOpen}
                onClose={closeLegendModal}
                geoDetails={geoDetails}
                svg={getSvgMap(legendId)}
                geoTag={geoTag}
                selectionActive={selectionActive}
            />

            {isModalOpen && <MetricsModal
                isOpen={isModalOpen}
                onClose={closeModal}
                geoDetails={geoDetails}
                geoTag={geoTag}
                pdfUrl={pdfUrl}
                logoUrl={logoUrl}
                legendUrl={legendUrl}
                selectedArea={selectedArea}
                siteUrl={siteUrl}
            />}

            {isMappableModalOpen && <MappableMetricsModal
                isOpen={isMappableModalOpen}
                onClose={closeMappableModal}
            />}

            {isLayersModalOpen && <LayersModal isOpen={isLayersModalOpen} onClose={handleShowLayers} />}

            {mapItemDetailModalOpen && <MapItemDetailsModal layerId={mapItemParams?.layerId} pointId={mapItemParams?.id} isOpen={mapItemDetailModalOpen} onClose={closeMapItemModal}/> }

            {isProgrammaticModalOpen && (
                <ProgrammaticModal
                isOpen={isProgrammaticModalOpen}
                onClose={closeProgrammaticModal}
                layers={layers}
                geoDetails={geoDetails}
                geoTag={geoTag}
                pdfUrl={pdfUrl}
                logoUrl={logoUrl}
                selectedArea={selectedArea}
                siteUrl={siteUrl}
                />
            )}

            {showDownloadLoading &&
                <DownloadLoading/>
            }
        </div>
    );
}

export default Dashboard;